import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import FileUpload from "@/components/admin/FileUpload";
import BulkLogoUpload from "@/components/admin/BulkLogoUpload";

const AdminSettings = () => {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/admin").then(({ data }) =>
      setForm({
        ...data,
        popup: {
          enabled: false,
          image: "",
          title: "",
          description: "",
          button_text: "Selengkapnya",
          button_link: "",
          popup_size: "md",
          image_aspect: "landscape",
          image_fit: "cover",
          ...(data.popup || {}),
        },
        certifications: Array.isArray(data.certifications) ? data.certifications : [],
        client_logos: Array.isArray(data.client_logos) ? data.client_logos : [],
      })
    );
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Pengaturan disimpan");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-zinc-500">Loading...</div>;

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const setSocial = (k, v) => setForm({ ...form, social: { ...(form.social || {}), [k]: v } });
  const setStat = (k, v) => setForm({ ...form, stats: { ...(form.stats || {}), [k]: v } });
  const setPopup = (k, v) => setForm({ ...form, popup: { ...(form.popup || {}), [k]: v } });
  const certs = form.certifications || [];
  const setCert = (id, patch) =>
    setField(
      "certifications",
      certs.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  const addCert = () =>
    setField("certifications", [
      ...certs,
      { id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `cert_${Date.now()}`, name: "", image: "" },
    ]);
  const removeCert = (id) => setField("certifications", certs.filter((c) => c.id !== id));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">Pengaturan Website</h1>
          <p className="mt-1 text-sm text-zinc-500">Kelola identitas, kontak, sosial media, SEO, popup, logo sertifikasi, logo klien, dan notifikasi email.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900 disabled:opacity-60">
          <Save size={16} /> {saving ? "Menyimpan..." : "Simpan"}
        </button>
      </div>

      <div className="grid gap-6">
        <Panel title="Identitas Website">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Nama Website" value={form.site_name} onChange={(v) => setField("site_name", v)} />
            <F label="Tagline" value={form.tagline} onChange={(v) => setField("tagline", v)} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <FileUpload label="Logo (untuk header & footer)" value={form.logo} onChange={(v) => setField("logo", v)} folder="logo" aspect="square" fit="contain" testid="upload-logo" />
            <FileUpload label="Favicon (otomatis dipotong jadi ikon persegi rapi)" value={form.favicon} onChange={(v) => setField("favicon", v)} folder="favicon" aspect="square" fit="contain" testid="upload-favicon" />
          </div>
        </Panel>

        <Panel title="Popup Beranda (Welcome)">
          <p className="text-sm text-zinc-500 mb-4">
            Popup muncul cepat di halaman beranda setiap kali dibuka atau di-refresh. Atur gambar, teks, tombol, dan ukuran tampilan.
          </p>
          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="popup_enabled"
              checked={!!form.popup?.enabled}
              onChange={(e) => setPopup("enabled", e.target.checked)}
              data-testid="popup-enabled"
            />
            <label htmlFor="popup_enabled" className="text-sm font-semibold text-zinc-800">Aktifkan popup</label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FileUpload
              label="Gambar Popup"
              value={form.popup?.image || ""}
              onChange={(v) => setPopup("image", v)}
              folder="popup"
              aspect="wide"
              fit={form.popup?.image_fit === "contain" ? "contain" : "cover"}
              testid="upload-popup"
            />
            <div className="space-y-4">
              <F label="Judul" value={form.popup?.title || ""} onChange={(v) => setPopup("title", v)} testid="popup-title" />
              <div>
                <label className="block text-sm font-semibold text-zinc-800 mb-2">Deskripsi</label>
                <textarea
                  rows={4}
                  value={form.popup?.description || ""}
                  onChange={(e) => setPopup("description", e.target.value)}
                  data-testid="popup-description"
                  placeholder="Tulis teks promo / pengumuman..."
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none"
                />
              </div>
              <F label="Teks Tombol" value={form.popup?.button_text || ""} onChange={(v) => setPopup("button_text", v)} testid="popup-button-text" />
              <F
                label="Link Tombol (contoh: /produk atau https://...)"
                value={form.popup?.button_link || ""}
                onChange={(v) => setPopup("button_link", v)}
                testid="popup-button-link"
              />
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-800 mb-2">Ukuran Popup</label>
              <select
                value={form.popup?.popup_size || "md"}
                onChange={(e) => setPopup("popup_size", e.target.value)}
                data-testid="popup-size"
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none bg-white"
              >
                <option value="sm">Kecil</option>
                <option value="md">Sedang</option>
                <option value="lg">Besar</option>
                <option value="xl">Sangat Besar</option>
              </select>
              <p className="mt-1.5 text-xs text-zinc-500">Dipakai saat mode “Isi penuh (crop)”.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-800 mb-2">Rasio Gambar</label>
              <select
                value={form.popup?.image_aspect || "landscape"}
                onChange={(e) => setPopup("image_aspect", e.target.value)}
                data-testid="popup-image-aspect"
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none bg-white"
              >
                <option value="auto">Otomatis (asli)</option>
                <option value="landscape">Landscape (16:10)</option>
                <option value="square">Persegi (1:1)</option>
                <option value="portrait">Portrait (3:4)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-800 mb-2">Mode Gambar</label>
              <select
                value={form.popup?.image_fit || "cover"}
                onChange={(e) => setPopup("image_fit", e.target.value)}
                data-testid="popup-image-fit"
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none bg-white"
              >
                <option value="cover">Isi penuh (crop)</option>
                <option value="contain">Utuh tanpa crop (hampir fullscreen)</option>
              </select>
            </div>
          </div>
        </Panel>

        <Panel title="Logo Sertifikasi">
          <p className="text-sm text-zinc-500 mb-4">
            Logo ini dipakai di marquee beranda (slide) dan grid sertifikasi di halaman Tentang (tanpa slide).
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certs.map((c, i) => (
              <div key={c.id} className="p-4 rounded-2xl border border-zinc-200 bg-zinc-50/60 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Logo {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeCert(c.id)}
                    className="text-xs font-semibold text-red-600 hover:text-red-700"
                  >
                    Hapus
                  </button>
                </div>
                <FileUpload
                  label="Gambar logo"
                  value={c.image || ""}
                  onChange={(v) => setCert(c.id, { image: v })}
                  folder="certifications"
                  aspect="square"
                  fit="contain"
                  testid={`upload-cert-${i}`}
                />
                <F
                  label="Nama (opsional)"
                  value={c.name || ""}
                  onChange={(v) => setCert(c.id, { name: v })}
                  testid={`cert-name-${i}`}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addCert}
            data-testid="add-certification"
            className="mt-4 inline-flex items-center px-4 py-2.5 rounded-full border border-maroon-200 text-maroon-800 text-sm font-semibold hover:bg-maroon-50"
          >
            + Tambah logo sertifikasi
          </button>
        </Panel>

        <Panel title="Logo Klien Maklon">
          <p className="text-sm text-zinc-500 mb-4">
            Tampil di bawah Testimoni Klien: baris atas slide ke kanan, baris bawah slide ke kiri. Cocok untuk ratusan logo (upload massal).
          </p>
          <BulkLogoUpload
            items={form.client_logos || []}
            onChange={(next) => setField("client_logos", next)}
            folder="clients"
            label="Logo klien"
            maxFiles={200}
          />
        </Panel>

        <Panel title="Kontak">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Email" value={form.email} onChange={(v) => setField("email", v)} />
            <F label="Telepon" value={form.phone} onChange={(v) => setField("phone", v)} />
            <F label="Nomor WhatsApp (628xxx)" value={form.whatsapp_number} onChange={(v) => setField("whatsapp_number", v)} testid="wa-number" />
            <F label="Jam Operasional" value={form.operating_hours} onChange={(v) => setField("operating_hours", v)} />
            <div className="sm:col-span-2"><F label="Alamat" value={form.address} onChange={(v) => setField("address", v)} /></div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-zinc-800 mb-2">Pesan WhatsApp Default</label>
              <textarea rows={3} value={form.whatsapp_template} onChange={(e) => setField("whatsapp_template", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none" />
            </div>
            <div className="sm:col-span-2"><F label="Google Maps Embed URL" value={form.google_maps_embed} onChange={(v) => setField("google_maps_embed", v)} /></div>
          </div>
        </Panel>

        <Panel title="Statistik Hero">
          <div className="grid sm:grid-cols-4 gap-4">
            <F label="Tahun Pengalaman" value={form.stats?.experience_years || ""} onChange={(v) => setStat("experience_years", v)} />
            <F label="Brand Klien" value={form.stats?.brands_served || ""} onChange={(v) => setStat("brands_served", v)} />
            <F label="Formula Sukses" value={form.stats?.formulas_created || ""} onChange={(v) => setStat("formulas_created", v)} />
            <F label="Produk Launching" value={form.stats?.products_launched || ""} onChange={(v) => setStat("products_launched", v)} />
          </div>
        </Panel>

        <Panel title="Sosial Media">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Instagram" value={form.social?.instagram || ""} onChange={(v) => setSocial("instagram", v)} />
            <F label="Facebook" value={form.social?.facebook || ""} onChange={(v) => setSocial("facebook", v)} />
            <F label="YouTube" value={form.social?.youtube || ""} onChange={(v) => setSocial("youtube", v)} />
            <F label="TikTok" value={form.social?.tiktok || ""} onChange={(v) => setSocial("tiktok", v)} />
            <F label="LinkedIn" value={form.social?.linkedin || ""} onChange={(v) => setSocial("linkedin", v)} />
          </div>
        </Panel>

        <Panel title="SEO Default">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="SEO Title" value={form.seo_title} onChange={(v) => setField("seo_title", v)} />
            <F label="SEO Description" value={form.seo_description} onChange={(v) => setField("seo_description", v)} />
            <div className="sm:col-span-2"><F label="SEO Keywords" value={form.seo_keywords} onChange={(v) => setField("seo_keywords", v)} /></div>
          </div>
        </Panel>

        <Panel title={<span className="flex items-center gap-2"><Mail size={16} /> Notifikasi Email SMTP</span>}>
          <p className="text-sm text-zinc-500 mb-4">
            Aktifkan agar setiap ada lead baru masuk, notifikasi otomatis dikirim ke email Anda.
            Gunakan SMTP dari provider domain Anda (contoh: Zoho Mail, Google Workspace, cPanel, Titan).
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="SMTP Host (misal: smtp.zoho.com / smtp.gmail.com)" value={form.smtp_host || ""} onChange={(v) => setField("smtp_host", v)} testid="smtp-host" />
            <F label="SMTP Port (587 TLS / 465 SSL)" value={form.smtp_port || 587} onChange={(v) => setField("smtp_port", parseInt(v) || 587)} type="number" />
            <F label="SMTP Username (email login)" value={form.smtp_user || ""} onChange={(v) => setField("smtp_user", v)} testid="smtp-user" />
            <F label="SMTP Password / App Password" value={form.smtp_password || ""} onChange={(v) => setField("smtp_password", v)} type="password" />
            <F label="From Email (misal: info@mandala.co.id)" value={form.smtp_from || ""} onChange={(v) => setField("smtp_from", v)} />
            <F label="Terima Notifikasi Di" value={form.smtp_notify_to || ""} onChange={(v) => setField("smtp_notify_to", v)} testid="smtp-notify-to" />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input type="checkbox" checked={!!form.smtp_enabled} onChange={(e) => setField("smtp_enabled", e.target.checked)} id="smtp_enabled" data-testid="smtp-enabled" />
            <label htmlFor="smtp_enabled" className="text-sm">Aktifkan notifikasi email untuk lead baru</label>
          </div>
        </Panel>
      </div>
    </div>
  );
};

const Panel = ({ title, children }) => (
  <div className="bg-white p-6 rounded-2xl border border-zinc-200">
    <h3 className="font-display font-bold text-lg text-zinc-900 mb-4">{title}</h3>
    {children}
  </div>
);

const F = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none" />
  </div>
);

export default AdminSettings;
